// src/services/purchaseOrderService.js
import api from './api';
import { config } from '../config'; // --- UPDATE (Procurement MOW+ v1): CSV export with the token
import { getAuthToken } from '../utils/auth'; // --- UPDATE

const BASE = '/purchase-orders';

// ─── List POs – returns plain array (backward-compatible) ────
// Any existing code that iterates the result (e.g. purchaseOrders.map())
// will continue to work unchanged.
const getAllPOs = (params = {}) => {
  return api.get(BASE, { params }).then(r => {
    const payload = r.data;
    // New backend wraps in { success, data, total }
    if (payload && Array.isArray(payload.data)) return payload.data;
    // Old backend / fallback – already an array
    if (Array.isArray(payload)) return payload;
    return [];
  });
};

// ─── Paginated list – returns { data[], total, limit, offset } 
// Used by PurchaseOrderManager internally for the paginated table.
const getPaginatedPOs = (params = {}) => {
  return api.get(BASE, { params }).then(r => {
    const payload = r.data;
    if (payload && Array.isArray(payload.data)) {
      return { data: payload.data, total: payload.total ?? payload.data.length, limit: params.limit ?? 20, offset: params.offset ?? 0 };
    }
    const arr = Array.isArray(payload) ? payload : [];
    return { data: arr, total: arr.length, limit: params.limit ?? 20, offset: params.offset ?? 0 };
  });
};

// ─── Single PO with line items ────────────────────────────────
const getPOById = (id) =>
  api.get(`${BASE}/${id}`).then(r => r.data.data);

// ─── Create PO  ───────────────────────────────────────────────
// poData  : { supplier_id, order_date, budget_code, priority, notes, expected_delivery_date }
// items   : [{ item_id, quantity, quoted_price, unit_price }]
const createPO = (poData, items) =>
  api.post(BASE, { poData, items }).then(r => r.data.data);

// ─── Status update ────────────────────────────────────────────
const updatePOStatus = (id, status) =>
  api.put(`${BASE}/${id}/status`, { status }).then(r => r.data);

// ─── Approve / Reject ─────────────────────────────────────────
const approvePO = (id) =>
  api.post(`${BASE}/${id}/approve`).then(r => r.data.data);

const rejectPO = (id, reason) =>
  api.post(`${BASE}/${id}/reject`, { reason }).then(r => r.data.data);

// ─── Notes (AI justification) ─────────────────────────────────
const updatePONotes = (id, notes) =>
  api.patch(`${BASE}/${id}/notes`, { notes }).then(r => r.data);

// ─── Digital Signature & Multi-Level Approval ─────────────────
const signPO = (id, payload) =>
  api.post(`${BASE}/${id}/sign`, payload).then(r => r.data);

const getPOSignatures = (id) =>
  api.get(`${BASE}/${id}/signatures`).then(r => r.data.data);

// ─── Analytics dashboard data ─────────────────────────────────
const getAnalytics = () =>
  api.get(`${BASE}/analytics`).then(r => r.data.data);

// ─── Dropdown data ────────────────────────────────────────────
const getSuppliers = () =>
  api.get(`${BASE}/suppliers`).then(r => r.data.data);

const getInventoryItems = (search = '') =>
  api.get(`${BASE}/inventory-items`, { params: { search } }).then(r => r.data.data);

// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
// updateNotes was called by PurchaseOrderDetailModal but did not exist (the edit crashed); receiving
// (full / partial), timeline, receipts, FlowSense procurement intel, draft check, learned lead time,
// supplier meta, CSV export, cancel; poErrorMessage gives the server's reason for any failure.
/** Edit (replace) or append the PO notes. */
const updateNotes = (id, notes, { replace = true } = {}) =>
  api.patch(`${BASE}/${id}/notes${replace ? '?mode=replace' : ''}`, { notes, ...(replace ? { mode: 'replace' } : {}) }).then(r => r.data);
const receivePO = (id, lines = null, note = '') =>
  api.post(`${BASE}/${id}/receive`, { lines, note }).then(r => r.data.data);
const getReceipts = (id) => api.get(`${BASE}/${id}/receipts`).then(r => r.data.data);
const getEvents = (id) => api.get(`${BASE}/${id}/events`).then(r => r.data.data);
const getIntel = ({ refresh = false } = {}) => api.get(`${BASE}/intel`, { params: refresh ? { refresh: 1 } : {} }).then(r => r.data.data);
const checkDraft = (body) => api.post(`${BASE}/check`, body).then(r => r.data.data);
const applyLeadTime = (supplierId, days = null) => api.post(`${BASE}/suppliers/${supplierId}/lead-time`, days != null ? { days } : {}).then(r => r.data.data);
const getSupplierMeta = () => api.get(`${BASE}/suppliers/meta`).then(r => r.data.data);
const cancelPO = (id) => updatePOStatus(id, 'Cancelled');
/** CSV of all purchase orders (sent with the signed-in token — a plain link would not carry it). */
const exportPOs = async () => {
  const res = await fetch(`${config.apiUrl}${BASE}/export`, { headers: { Authorization: `Bearer ${getAuthToken() || ''}` } });
  if (!res.ok) { let m = `Export failed (${res.status})`; try { m = (await res.json()).message || m; } catch (_) { /* */ } throw new Error(m); }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `flowsense-purchase-orders-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) { /* */ } }, 1500);
};
export const poErrorMessage = (e) => e?.response?.data?.message || e?.body?.message || e?.message || 'Request failed';
// --- END UPDATE

const purchaseOrderService = {
  getAllPOs,         // ← plain array, backward-compatible
  getPaginatedPOs,   // ← { data, total } for paginated table
  getPOById,
  createPO,
  updatePOStatus,
  approvePO,
  rejectPO,
  updatePONotes,
  signPO,            // ← NEW: Submit digital signature
  getPOSignatures,   // ← NEW: Fetch signature ledger
  getAnalytics,
  getSuppliers,
  getInventoryItems,
  // --- UPDATE (Procurement MOW+ v1)
  updateNotes, receivePO, getReceipts, getEvents, getIntel, checkDraft, applyLeadTime, getSupplierMeta, cancelPO, exportPOs,
  // --- END UPDATE
};

export default purchaseOrderService;